import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apis from '../../apis';
import apcLogo from '../../assets/images/apc_logo.svg';

import useTicketEventData from '../../hooks/useTicketEventData';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import './PublicTicketBookingPage.css';

const formatRupiah = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const STEPS = ['Select Entry', 'Select Tickets', 'Your Details', 'Review & Pay'];
const CHECKOUT_ATTEMPT_STORAGE_KEY = 'apcs.publicTicket.checkoutAttempt';

const getBookingAwardLabel = award => String(award || '').trim().toLowerCase() === 'sapphire' ? 'Diamond' : String(award || '').trim();

const getBuyerEligibility = (eligibility, today) => {
  if (!eligibility?.enabled) return { isPublicEnabled: true, isWinnerEnabled: true };
  const allowedTiers = (eligibility.schedule || [])
    .filter(entry => entry.date === today)
    .flatMap(entry => entry.allowedTiers || []);
  return {
    isPublicEnabled: allowedTiers.includes('Public'),
    isWinnerEnabled: ['Sapphire', 'Diamond', 'Gold', 'Silver'].some(award => allowedTiers.includes(award)),
  };
};

const isWinnerEnsemble = (winner) => {
  if (!winner) return false;
  if (winner.isEnsemble !== undefined) return !!winner.isEnsemble;
  if ((winner.performanceCategory || '').trim().toLowerCase() === 'ensemble') return true;
  if ((winner.competitionCategory || '').toLowerCase().includes('ensemble')) return true;
  if ((winner.performerNames || []).length > 1) return true;
  return false;
};

const getWinnerPerformerNames = (winner) => {
  if (!winner) return [];
  if (Array.isArray(winner.performerNames) && winner.performerNames.length > 0) {
    return winner.performerNames;
  }
  if (Array.isArray(winner.performers) && winner.performers.length > 0) {
    return winner.performers.map(p => {
      if (typeof p === 'string') return p;
      return (p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name || '').trim();
    }).filter(Boolean);
  }
  return winner.name ? [winner.name] : [];
};

// --- Helper: Restrict ticket tiers based on buyer type ---
const isTierAllowedForBuyer = (tier, buyerType) => {
  if (!tier) return false;
  const tierId = (tier.id || '').trim().toLowerCase();
  const tierName = (tier.name || '').trim().toLowerCase();
  if (buyerType === 'public') {
    return (
      tierId === 'presto' ||
      tierName === 'presto' ||
      tierId === 'allegro' ||
      tierId === 'alegro' ||
      tierName === 'allegro' ||
      tierName === 'alegro'
    );
  }
  return !/master[ _-]?class/i.test(`${tierId} ${tierName}`);
};

export default function PublicTicketBookingPage() {

  const navigate = useNavigate();
  const { event, loading: eventLoading, error: eventError } = useTicketEventData();

  const dynamicVenueInfo = useMemo(() => {
    const info = {};
    (event?.venues || []).forEach(v => {
      info[v.id] = { label: v.label, img: v.imageUrl };
    });
    return info;
  }, [event]);

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const topRef = useRef(null);
  const checkoutIdempotencyKeyRef = useRef(null);

  // Step 0 - Winner Selection
  const [winners, setWinners] = useState([]);
  const [publicPerformances, setPublicPerformances] = useState([]);
  const [winnersLoaded, setWinnersLoaded] = useState(false);
  const [publicPerformancesLoaded, setPublicPerformancesLoaded] = useState(false);
  const [winnersLoading, setWinnersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWinner, setSelectedWinner] = useState(null);

  const [buyerType, setBuyerType] = useState('public'); // 'public' or 'winner'
  const [publicSelectionType, setPublicSelectionType] = useState('competition');
  const [manualPayment, setManualPayment] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState('');

  const [venue, setVenue] = useState('');
  const [date, setDate] = useState('');
  const [session, setSession] = useState('');

  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  const [eligibilityError, setEligibilityError] = useState('');

  const { isPublicEnabled, isWinnerEnabled } = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' });
    return getBuyerEligibility(eligibility, today);
  }, [eligibility]);
  const isBuyerTypeEnabled = buyerType === 'public' ? isPublicEnabled : isWinnerEnabled;

  const allSessions = useMemo(() => {
    const sessionsList = [];
    const specialSessionSlots = new Set();

    // Add orchestra sessions
    (event?.orchestraSessions || []).forEach(os => {
      specialSessionSlots.add(`${os.venue}|${os.date}|${os.time}`);
      sessionsList.push({
        ...os,
        isOrchestra: true,
        label: `[Orchestra] ${dynamicVenueInfo[os.venue]?.label || os.venue} | ${os.date} | ${os.time}`
      });
    });

    // Exclude retired Masterclass slots from ordinary competition sales
    (event?.masterclassSessions || []).forEach(ms => {
      specialSessionSlots.add(`${ms.venue}|${ms.date}|${ms.time}`);
    });

    // Add normal competition sessions
    (event?.venues || []).forEach(v => {
      if (v.sessions) {
        Object.entries(v.sessions).forEach(([d, times]) => {
          (times || []).forEach(t => {
            if (specialSessionSlots.has(`${v.id}|${d}|${t}`)) return;
            const sId = `${v.id}_${d}_${t}`;
            sessionsList.push({
              id: sId,
              venue: v.id,
              date: d,
              time: t,
              isOrchestra: false,
              complimentaryQuota: 0,
              complimentaryClaimed: 0,
              reservedRows: [],
              label: `${v.label || v.id} | ${d} | ${t}`
            });
          });
        });
      }
    });

    return sessionsList;
  }, [event, dynamicVenueInfo]);

  const selectedSessionObj = useMemo(() => allSessions.find(s => s.id === selectedSessionId), [allSessions, selectedSessionId]);

  useEffect(() => {
    if (buyerType === 'public' && publicSelectionType === 'orchestra' && selectedSessionObj) {
      setVenue(selectedSessionObj.venue);
      setDate(selectedSessionObj.date);
      setSession(selectedSessionObj.time);
    }
  }, [buyerType, publicSelectionType, selectedSessionObj]);

  useEffect(() => {
    if ((buyerType === 'winner' || (buyerType === 'public' && publicSelectionType === 'competition')) && selectedWinner?.session) {
      setVenue(selectedWinner.session.venue);
      setDate(selectedWinner.session.date);
      setSession(selectedWinner.session.time);
    }
  }, [buyerType, publicSelectionType, selectedWinner]);

  // Step 1 – seat picking & add-ons
  const [seatLayout, setSeatLayout] = useState([]);
  const [seatLayoutLoading, setSeatLayoutLoading] = useState(false);
  const [selections, setSelections] = useState({});   // { tierId: [seatObj] }
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [quantities, setQuantities] = useState({});

  const availableAddOns = useMemo(() => (event?.addOns || []).filter(addOn =>
    addOn.id !== 'seat_selection'
    && !/master[ _-]?class/i.test(`${addOn.id} ${addOn.name}`)
    && (buyerType === 'winner'
      || (publicSelectionType === 'competition' && addOn.id === 'seat_selection_performer'))
  ), [event, buyerType, publicSelectionType]);

  const totalTicketsPurchased = useMemo(() =>
    Object.values(quantities).reduce((a, b) => a + (b || 0), 0),
    [quantities]);

  // Total seats selected
  const totalSelected = useMemo(() =>
    Object.values(selections).reduce((s, arr) => s + arr.length, 0),
    [selections]
  );

  const resetPurchaseChoices = useCallback(() => {
    setSelections({});
    setSelectedAddOns([]);
    setQuantities({});
    setSeatLayout([]);
    setSubmitError('');
    setManualPayment(false);
    checkoutIdempotencyKeyRef.current = null;
    window.sessionStorage.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
  }, []);

  const handleBuyerTypeChange = useCallback((nextBuyerType) => {
    if (nextBuyerType === 'public' ? !isPublicEnabled : !isWinnerEnabled) return;
    if (nextBuyerType === buyerType) return;
    resetPurchaseChoices();
    setBuyerType(nextBuyerType);
    setSelectedSessionId('');
    setSelectedWinner(null);
    setRegistrantName('');
    setBuyerName('');
    setUserEmail('');
    setUserPhone('');
    setVenue('');
    setDate('');
    setSession('');
    setSearchTerm('');
    setStep(0);
  }, [buyerType, isPublicEnabled, isWinnerEnabled, resetPurchaseChoices]);

  const handleSessionChange = (nextSessionId) => {
    if (nextSessionId === selectedSessionId) return;
    resetPurchaseChoices();
    setSelectedSessionId(nextSessionId);
  };

  const handlePublicSelectionTypeChange = (nextType) => {
    if (nextType === publicSelectionType) return;
    resetPurchaseChoices();
    setPublicSelectionType(nextType);
    setSelectedWinner(null);
    setSelectedSessionId('');
    setRegistrantName('');
    setVenue('');
    setDate('');
    setSession('');
  };

  // Step 2
  const [registrantName, setRegistrantName] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    if (eligibilityLoading || eligibilityError || isBuyerTypeEnabled) return;
    if (isPublicEnabled) handleBuyerTypeChange('public');
    else if (isWinnerEnabled) handleBuyerTypeChange('winner');
  }, [eligibilityLoading, eligibilityError, isBuyerTypeEnabled, isPublicEnabled, isWinnerEnabled, handleBuyerTypeChange]);

  // Fetch only the list for the visible buyer path; the other list is loaded on demand.
  useEffect(() => {
    if (eligibilityLoading || eligibilityError || !isBuyerTypeEnabled) {
      setWinnersLoading(false);
      return;
    }
    if ((buyerType === 'public' && publicPerformancesLoaded)
      || (buyerType === 'winner' && winnersLoaded)) {
      setWinnersLoading(false);
      return;
    }
    let canceled = false;
    const fetchWinners = async () => {
      setWinnersLoading(true);
      try {
        const result = await apis.publicTicket.getEligibleWinners(buyerType);
        if (result?.error) throw new Error('Failed to load performances.');
        if (canceled) return;
        if (buyerType === 'public') {
          setPublicPerformances(result.data?.winners || []);
          setPublicPerformancesLoaded(true);
        } else {
          setWinners(result.data?.winners || []);
          setWinnersLoaded(true);
        }
      } catch (err) {
        console.error('Fetch winners error', err);
      } finally {
        if (!canceled) setWinnersLoading(false);
      }
    };
    fetchWinners();
    return () => { canceled = true; };
  }, [buyerType, winnersLoaded, publicPerformancesLoaded, eligibilityLoading, eligibilityError, isBuyerTypeEnabled]);

  useEffect(() => {
    let canceled = false;
    const fetchEligibility = async () => {
      try {
        const res = await apis.systemSettings.getGlobalSettings();
        if (res?.error || !res?.data) throw new Error('Unable to verify ticket sale eligibility.');
        if (!canceled) setEligibility(res.data.ticketEligibility || null);
      } catch (err) {
        console.error('Fetch eligibility error', err);
        if (!canceled) setEligibilityError('Unable to verify ticket sale eligibility. Please refresh the page.');
      } finally {
        if (!canceled) setEligibilityLoading(false);
      }
    };
    fetchEligibility();
    return () => { canceled = true; };
  }, []);

  // Fetch live seat layout when entering step 1
  useEffect(() => {
    if (step !== 1 || !venue || !date || !session || (buyerType === 'public' && selectedSessionObj?.isOrchestra)) return;
    let canceled = false;
    const fetchSeats = async () => {
      setSeatLayoutLoading(true);
      try {
        const sessionId = `${date}_${session}`;
        const res = await apis.publicTicket.getSeats(venue, sessionId);
        console.log("res seats", res)
        if (res?.error) throw new Error('Failed to load seats.');
        if (!canceled) setSeatLayout(res.data || []);
      } catch (e) {
        console.error('Seat fetch error', e);
      } finally {
        if (!canceled) setSeatLayoutLoading(false);
      }
    };
    fetchSeats();
    return () => { canceled = true; };
  }, [step, venue, date, session, buyerType, selectedSessionObj]);

  const tierColors = useMemo(() => {
    if (!event?.ticketTiers) {
      // Fallback
      return {
        'presto': '#EBBC64',
        'allegro': '#C0C0C0',
        'lento': '#CD7F32',
      };
    }
    return event.ticketTiers.reduce((acc, t) => {
      acc[t.id.toLowerCase()] = t.color || '#888888';
      return acc;
    }, {});
  }, [event?.ticketTiers]);

  const rightSideMax = useMemo(() => ({
    A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
    G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
    N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
  }), []);

  // Format seat layout as a single unified theater map
  const unifiedLayout = useMemo(() => {
    if (!seatLayout.length) return [];

    // Group all seats by row first, ignoring tier
    const byRow = seatLayout.reduce((acc, s) => {
      if (!acc[s.row]) acc[s.row] = [];
      acc[s.row].push(s);
      return acc;
    }, {});

    // Sort rows alphabetically (A, B, C...)
    const sortedRowKeys = Object.keys(byRow).sort((a, b) => a.localeCompare(b));

    return sortedRowKeys.map(row => {
      // Sort seats within row by number descending (right to left)
      byRow[row].sort((a, b) => b.number - a.number);

      const mappedRow = byRow[row].map(s => {
        const normalizedAreaType = s.areaType?.toLowerCase();
        return {
          id: s.id, number: s.number, row: s.row, areaType: s.areaType, seatLabel: s.seatLabel,
          isReserved: s.status !== 'available',
          color: tierColors[normalizedAreaType] || '#888888',
          tooltip: `${s.seatLabel} – ${s.status !== 'available' ? 'Taken' : 'Available'}`,
        };
      });

      if (venue === 'Venue1') {
        const splitSeatNumber = rightSideMax[row];
        if (splitSeatNumber) {
          const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
          if (splitIndex !== -1) {
            // Insert two nulls for a wide aisle
            mappedRow.splice(splitIndex, 0, null, null);
          }
        }
      }

      return mappedRow;
    }).filter(r => r.length > 0);
  }, [seatLayout, tierColors, rightSideMax, venue]);

  // All selected seat IDs (flat)
  const allSelectedSeatIds = useMemo(() =>
    Object.values(selections).flat().map(s => s.id),
    [selections]
  );

  // Helper to get venue-specific price for a tier
  const getVenuePrice = useCallback((tier) => {
    if (!venue || !tier) return null;
    return tier.venuePrices?.[venue] ?? null;
  }, [venue]);

  // Check if all tiers have pricing configured for the selected venue
  const hasMissingPricing = useMemo(() => {
    if (!venue || !event?.ticketTiers) return false;
    const applicableTiers = event.ticketTiers.filter(t => isTierAllowedForBuyer(t, buyerType));
    return applicableTiers.some(t => {
      const price = getVenuePrice(t);
      return price === undefined || price === null;
    });
  }, [event, venue, buyerType, getVenuePrice]);

  const seatSelectionPerformerCount = useMemo(() =>
    selectedAddOns.filter(id => id === 'seat_selection_performer').length,
    [selectedAddOns]);


  const orderTotal = useMemo(() => {
    let total = 0;
    (event?.ticketTiers || []).forEach(t => {
      const normalizedId = (t.id || '').toLowerCase();
      const venuePrice = getVenuePrice(t);
      if (venuePrice !== null) {
        total += (quantities[normalizedId] || 0) * Number(venuePrice);
      }
    });
    availableAddOns.forEach(a => {
      const qty = selectedAddOns.filter(id => id === a.id).length;
      if (qty > 0) {
        total += Number(a.price) * qty;
      }
    });
    return total;
  }, [event, quantities, selectedAddOns, availableAddOns, getVenuePrice]);

  // Only keep add-ons supported by the current purchase path.
  useEffect(() => {
    const allowedAddOns = selectedAddOns.filter(id => availableAddOns.some(addOn => addOn.id === id));
    if (allowedAddOns.length !== selectedAddOns.length) {
      setSelectedAddOns(allowedAddOns);
    }
  }, [availableAddOns, selectedAddOns]);

  // Ensure public buyers cannot hold quantities for tiers other than Presto and Allegro
  useEffect(() => {
    if (buyerType === 'public') {
        setQuantities(prev => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach(tierKey => {
          const dummyTier = { id: tierKey, name: tierKey };
          if (!isTierAllowedForBuyer(dummyTier, 'public') && next[tierKey] > 0) {
            delete next[tierKey];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [buyerType, selectedSessionObj]);

  // Build tickets array for submission
  const ticketsPayload = useMemo(() => {
    const qtyCopy = { ...quantities };

    return (event?.ticketTiers || [])
      .filter(t => {
        if (!isTierAllowedForBuyer(t, buyerType)) return false;
        const normalizedId = (t.id || '').toLowerCase();
        return (qtyCopy[normalizedId] || 0) > 0;
      })
      .map(t => {
        const venuePrice = getVenuePrice(t);
        return { id: t.id, name: t.name, quantity: qtyCopy[(t.id || '').toLowerCase()], priceEach: venuePrice ?? 0 };
      });
  }, [event, quantities, buyerType, getVenuePrice]);

  const canProceedStep0 = buyerType === 'public'
    ? (publicSelectionType === 'orchestra' ? !!selectedSessionId : !!selectedWinner)
    : !!selectedWinner;
  const seatDataMissing = !seatLayoutLoading && step === 1 && seatLayout.length === 0;
  const canProceedStep1 = !hasMissingPricing && (!seatDataMissing || seatSelectionPerformerCount === 0) && totalSelected === seatSelectionPerformerCount && totalTicketsPurchased > 0;
  const logicalStepDetails = 2;
  const canProceedStepDetails = buyerName.trim() && (buyerType === 'winner' ? registrantName.trim() : true) && userEmail.trim() && userPhone.trim();

  const goNext = () => { setStep(s => s + 1); scrollTop(); };
  const goBack = () => { setStep(s => s - 1); scrollTop(); };

  const handleSelectWinner = (winner) => {
    if (winner.registrantId !== selectedWinner?.registrantId) resetPurchaseChoices();
    setSelectedWinner(winner);
    const pNames = getWinnerPerformerNames(winner);
    const isEnsemble = isWinnerEnsemble(winner);
    const displayName = isEnsemble && pNames.length > 0
      ? pNames.join(', ')
      : (winner.name || '');
    setRegistrantName(displayName);
    if (buyerType === 'winner') {
      setBuyerName(winner.name || '');
      setUserEmail(winner.email || '');
    }
  };

  const filteredWinners = useMemo(() => {
    const available = buyerType === 'public' ? publicPerformances : winners;
    if (!searchTerm) return available;
    const lower = searchTerm.toLowerCase();
    return available.filter(w => {
      const pNames = getWinnerPerformerNames(w);
      return (
        w.name?.toLowerCase().includes(lower) ||
        w.teacherName?.toLowerCase().includes(lower) ||
        pNames.some(n => n?.toLowerCase().includes(lower))
      );
    });
  }, [winners, publicPerformances, buyerType, searchTerm]);

  const handleSelectSeat = (seat) => {
    const tierId = seat.areaType?.toLowerCase();
    const currentSelectedInTier = (selections[tierId] || []).length;
    if (currentSelectedInTier >= (quantities[tierId] || 0)) {
      Modal.warning({ title: 'Limit Exceeded', content: `You cannot select more ${seat.areaType} seats than you purchased tickets for.` });
      return;
    }
    if (totalSelected >= seatSelectionPerformerCount) {
      Modal.warning({ title: 'Limit Exceeded', content: `You can only select up to ${seatSelectionPerformerCount} seats based on your Add-on quantity.` });
      return;
    }
    setSelections(p => ({ ...p, [tierId]: [...(p[tierId] || []), seat] }));
  };
  const handleDeselectSeat = (seat) => {
    const tierId = seat.areaType?.toLowerCase();
    setSelections(p => ({ ...p, [tierId]: (p[tierId] || []).filter(s => s.id !== seat.id) }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        registrantName, buyerName, userEmail, userPhone,
        venue, date, session,
        isOrchestra: buyerType === 'public' && publicSelectionType === 'orchestra',
        isPublic: buyerType === 'public',
        bookingType: buyerType === 'winner' ? 'winner'
          : publicSelectionType === 'orchestra' ? 'public_orchestra' : 'public_competition',
        manualPayment,
        tickets: ticketsPayload,
        selectedSeatIds: allSelectedSeatIds,
        performanceSeatLabels: Object.values(selections).flat().map(s => s.seatLabel),
        addOnIds: selectedAddOns.filter(id => availableAddOns.some(addOn => addOn.id === id)),
        registrantId: buyerType === 'public' && publicSelectionType === 'orchestra'
          ? undefined : selectedWinner?.registrantId,
      };
      const attemptFingerprint = JSON.stringify({
        registrantId: payload.registrantId || '', bookingType: payload.bookingType,
        manualPayment, venue, date, session, tickets: ticketsPayload,
        selectedSeatIds: [...allSelectedSeatIds].sort(),
        addOnIds: [...payload.addOnIds].sort(), buyerName: buyerName.trim(),
        userEmail: userEmail.trim().toLowerCase(), userPhone: userPhone.trim(),
      });
      const savedAttempt = JSON.parse(window.sessionStorage.getItem(CHECKOUT_ATTEMPT_STORAGE_KEY) || 'null');
      const idempotencyKey = savedAttempt?.fingerprint === attemptFingerprint
        ? savedAttempt.key
        : window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      checkoutIdempotencyKeyRef.current = idempotencyKey;
      window.sessionStorage.setItem(CHECKOUT_ATTEMPT_STORAGE_KEY, JSON.stringify({ key: idempotencyKey, fingerprint: attemptFingerprint }));
      payload.idempotencyKey = idempotencyKey;
      const res = await apis.publicTicket.createBooking(payload);
      if (res?.error) throw new Error(res.error?.data?.message || 'Booking failed.');
      const { bookingId, paymentUrl, paymentMode } = res.data;
      if (paymentMode === 'manual') {
        navigate(`/ticket-payment-instructions/${bookingId}`);
        checkoutIdempotencyKeyRef.current = null;
        window.sessionStorage.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
        return;
      }
      if (paymentUrl) window.open(paymentUrl, '_blank');
      navigate(`/waiting-payment/${bookingId}`, { state: { paymentLink: paymentUrl, isPublicTicket: true } });
      checkoutIdempotencyKeyRef.current = null;
      window.sessionStorage.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message || 'Booking failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (eventLoading || eligibilityLoading || (!isBuyerTypeEnabled && (isPublicEnabled || isWinnerEnabled))) return (
    <div className="ptb-loading">
      <div className="ptb-spinner" />
      <p>Loading event details…</p>
    </div>
  );

  if (eventError) return (
    <div className="ptb-error">
      <p>Unable to load event data. Please refresh the page.</p>
    </div>
  );

  if (eligibilityError) return (
    <div className="ptb-error"><p>{eligibilityError}</p></div>
  );

  if (!isPublicEnabled && !isWinnerEnabled) return (
    <div className="ptb-error"><p>Ticket sales are closed today. Please check the sales schedule or contact APCS.</p></div>
  );


  const renderCartSummaryInner = () => (
    <>
      <h3>Order Summary</h3>
      {totalTicketsPurchased === 0 ? (
        <div className="ptb-empty-cart">No tickets selected yet.</div>
      ) : (
        <div className="ptb-cart-items">
          <>
            {(event?.ticketTiers || []).map(tier => {
              const normalizedId = tier.id.toLowerCase();
              const qty = quantities[normalizedId] || 0;
              if (qty === 0) return null;
              const venuePrice = getVenuePrice(tier);
              return (
                <div key={tier.id} className="ptb-cart-row">
                  <span>{qty}x {tier.name}</span>
                  <span>{venuePrice !== null ? formatRupiah(qty * venuePrice) : 'N/A'}</span>
                </div>
              );
            })}
          </>
          {availableAddOns.length > 0 && (() => {
            const uniqueAddOnIds = [...new Set(selectedAddOns)];
            return uniqueAddOnIds.map(addonId => {
              const a = event?.addOns?.find(x => x.id === addonId);
              if (!a) return null;
              const qty = selectedAddOns.filter(id => id === addonId).length;
              return (
                <div key={a.id} className="ptb-cart-row ptb-cart-addon">
                  <span>{qty}x {a.name}</span>
                  <span>{formatRupiah(a.price * qty)}</span>
                </div>
              );
            });
          })()}
          <div className="ptb-cart-total">
            <span>Total</span>
            <span>{formatRupiah(orderTotal)}</span>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="ptb-root" ref={topRef}>
      {/* ── Google Fonts ── */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <header className="ptb-header">
        <div className="ptb-header-inner">
          <img className="ptb-brand-logo" src={apcLogo} alt="APCS Logo" width="112" height="56" />
          <span className="ptb-subtitle">Gala Concert Tickets</span>
        </div>
      </header>

      {/* ── Progress ── */}
      <div className="ptb-stepper">
        {STEPS.map((label, i) => (
          <div key={i} className={`ptb-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
            <div className="ptb-step-circle">{i < step ? '✓' : i + 1}</div>
            <span className="ptb-step-label">{label}</span>
            {i < STEPS.length - 1 && <div className="ptb-step-line" />}
          </div>
        ))}
      </div>

      {/* ── Content ── */}
      <main className="ptb-main">

        {/* STEP 0 — Select Winner */}
        {step === 0 && (
          <div className="ptb-card ptb-animate">
            <h2 className="ptb-card-title">Identify Your Entry</h2>
            <p className="ptb-card-subtitle">Choose the performance you want to watch, or buy a direct orchestra ticket.</p>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
              {isPublicEnabled && (
                <button
                  className={`ptb-btn-ghost ${buyerType === 'public' ? 'active-tab' : ''}`}
                  onClick={() => handleBuyerTypeChange('public')}
                  style={{ flex: 1, backgroundColor: buyerType === 'public' ? '#EBBC64' : 'transparent', color: buyerType === 'public' ? '#000' : '#EBBC64' }}
                >
                  Public Buyer
                </button>
              )}
              {isWinnerEnabled && <button
                className={`ptb-btn-ghost ${buyerType === 'winner' ? 'active-tab' : ''}`}
                onClick={() => handleBuyerTypeChange('winner')}
                style={{ flex: 1, backgroundColor: buyerType === 'winner' ? '#EBBC64' : 'transparent', color: buyerType === 'winner' ? '#000' : '#EBBC64' }}
              >
                Registered Winner
              </button>}
            </div>

            {buyerType === 'public' && (
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                <button className={`ptb-btn-ghost ${publicSelectionType === 'competition' ? 'active-tab' : ''}`}
                  onClick={() => handlePublicSelectionTypeChange('competition')}
                  style={{ flex: 1, backgroundColor: publicSelectionType === 'competition' ? '#EBBC64' : 'transparent', color: publicSelectionType === 'competition' ? '#000' : '#EBBC64' }}>
                  Watch a Performance
                </button>
                <button className={`ptb-btn-ghost ${publicSelectionType === 'orchestra' ? 'active-tab' : ''}`}
                  onClick={() => handlePublicSelectionTypeChange('orchestra')}
                  style={{ flex: 1, backgroundColor: publicSelectionType === 'orchestra' ? '#EBBC64' : 'transparent', color: publicSelectionType === 'orchestra' ? '#000' : '#EBBC64' }}>
                  Orchestra Ticket
                </button>
              </div>
            )}

            {(buyerType === 'winner' || publicSelectionType === 'competition') && (
              <>
                <div className="ptb-search-box">
                  <SearchOutlined className="ptb-search-icon" />
                  <input
                    type="text"
                    placeholder="Search by winner, performer, or teacher name..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="ptb-search-input"
                  />
                </div>

                {winnersLoading ? (
                  <div className="ptb-winner-loading">
                    <div className="ptb-spinner-sm" />
                    <p>Loading eligible winners...</p>
                  </div>
                ) : (
                  <div className="ptb-winner-grid">
                    {filteredWinners.length > 0 ? (
                      filteredWinners.map(w => (
                        <div
                          key={w.registrantId}
                          className={`ptb-winner-card ${selectedWinner?.registrantId === w.registrantId ? 'selected' : ''}`}
                          style={{ marginTop: 16 }}
                          onClick={() => handleSelectWinner(w)}
                        >
                          <div className="ptb-winner-info">
                            <div className="ptb-winner-header">
                              <span className={`ptb-award-tag ${getBookingAwardLabel(w.finalAward).toLowerCase()}`}>
                                {getBookingAwardLabel(w.finalAward)}
                              </span>
                              <span className="ptb-winner-category">{w.competitionCategory}</span>
                              {isWinnerEnsemble(w) && (
                                <span className="ptb-ensemble-badge">
                                  Ensemble ({getWinnerPerformerNames(w).length})
                                </span>
                              )}
                            </div>
                            <h3 className="ptb-winner-name">
                              {isWinnerEnsemble(w) && getWinnerPerformerNames(w).length > 1
                                ? getWinnerPerformerNames(w).join(' & ')
                                : w.name}
                            </h3>
                            {isWinnerEnsemble(w) && getWinnerPerformerNames(w).length > 0 && (
                              <div className="ptb-ensemble-members">
                                <span className="ptb-ensemble-label">
                                  Registrant Names ({getWinnerPerformerNames(w).length}):
                                </span>
                                <div className="ptb-ensemble-names">
                                  {getWinnerPerformerNames(w).map((pName, pIdx) => {
                                    const isMatch = searchTerm && pName.toLowerCase().includes(searchTerm.toLowerCase());
                                    return (
                                      <span
                                        key={pIdx}
                                        className={`ptb-ensemble-member-tag ${isMatch ? 'highlighted' : ''}`}
                                      >
                                        {pName}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            <p className="ptb-winner-teacher">Teacher: {w.teacherName}</p>
                          </div>
                          <div className="ptb-winner-assignment">
                            <div className="ptb-assignment-row">
                              <span className="ptb-label">Venue:</span>
                              <span className="ptb-value">{dynamicVenueInfo[w.session?.venue]?.label || w.session?.venue}</span>
                            </div>
                            <div className="ptb-assignment-row">
                              <span className="ptb-label">Session:</span>
                              <span className="ptb-value">{w.session?.date} | {w.session?.time}</span>
                            </div>
                          </div>
                          {selectedWinner?.registrantId === w.registrantId && (
                            <div className="ptb-selected-check">✓ Selected</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="ptb-no-results">
                        <UserOutlined style={{ fontSize: '2rem', color: '#444', marginBottom: '1rem' }} />
                        <p>No performances found matching your search.</p>
                        <p className="ptb-help-text">Can't find the performance? Please contact our admin for assistance.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {buyerType === 'public' && publicSelectionType === 'orchestra' && <div style={{ marginTop: '2rem' }}>
              <h3 style={{ color: '#fff', marginBottom: '1rem' }}>
                Select Session
              </h3>
              <select
                className="ptb-input"
                value={selectedSessionId}
                onChange={(e) => handleSessionChange(e.target.value)}
              >
                <option value="">-- Choose a Session --</option>
                {allSessions.filter(os => os.isOrchestra)
                  .map(os => (
                    <option key={os.id} value={os.id}>
                      {os.label}
                    </option>
                  ))}
              </select>
            </div>}

            <div className="ptb-actions" style={{ marginTop: '2rem' }}>
              <button className="ptb-btn-primary" disabled={!canProceedStep0} onClick={() => setStep(1)}>
                Next: Select Tickets &rarr;
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — Seat Selection & Add-ons */}
        {step === 1 && (
          <div className="ptb-card ptb-animate ptb-step-fused">
            <h2 className="ptb-card-title">{buyerType === 'public' ? 'Select Tickets' : 'Select Seats & Add-ons'}</h2>
            {buyerType === 'public' && selectedSessionObj?.isOrchestra && <p className="ptb-card-subtitle">Free seating within your Presto or Allegro category.</p>}
            {buyerType === 'public' && publicSelectionType === 'competition' && <p className="ptb-card-subtitle">Each paid competition ticket includes one orchestra place for the selected performance. Performer places are not added to public bookings.</p>}


            <div className="ptb-fused-layout">
              <div className="ptb-fused-main">

                {/* 1. Ticket Quantities */}
                <div className="ptb-tier-section ptb-tier-section-fused" style={{ marginBottom: '2rem' }}>
                  <div className="ptb-tier-header">
                    <h3 className="ptb-section-label">Select Tickets</h3>
                  </div>
                  <div className="ptb-ticket-quantities" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {(event?.ticketTiers || [])
                      .filter(t => isTierAllowedForBuyer(t, buyerType))
                      .map(t => {
                        const tierId = (t.id || '').toLowerCase();
                        const venuePrice = getVenuePrice(t);
                        const qty = quantities[tierId] || 0;
                        return (
                          <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#2d3748', padding: '1rem', borderRadius: '8px' }}>
                            <div>
                              <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>{t.name}</span>
                              <span style={{ display: 'block', color: '#a0aec0', fontSize: '0.9rem' }}>{venuePrice !== null ? formatRupiah(venuePrice) : 'Price N/A'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: '#1a202c', padding: '4px 8px', borderRadius: '4px' }}>
                              <button className="ptb-btn-ghost" style={{ border: 'none', fontSize: '1.5rem', lineHeight: 1, padding: '0 8px' }} onClick={() => {
                                const currentSelected = (selections[tierId] || []).length;
                                if (qty - 1 < currentSelected) {
                                  Modal.warning({ title: 'Cannot Reduce Quantity', content: `Please deselect ${t.name} seats on the map below first before reducing quantity.` });
                                  return;
                                }
                                setQuantities(prev => ({ ...prev, [tierId]: Math.max(0, qty - 1) }));
                              }}>-</button>
                              <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 'bold', fontSize: '1.2rem' }}>{qty}</span>
                              <button className="ptb-btn-ghost" style={{ border: 'none', fontSize: '1.5rem', lineHeight: 1, padding: '0 8px' }} onClick={() => {
                                setQuantities(prev => ({ ...prev, [tierId]: qty + 1 }));
                              }}>+</button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* 2. Add-ons for the current purchase path */}
                {availableAddOns.length > 0 && (
                  <div className="ptb-addons-section" style={{ marginBottom: '2rem' }}>
                    <h3 className="ptb-section-label">Add-ons (Optional)</h3>
                    <div className="ptb-addon-list">
                      {availableAddOns.map(a => {
                        const qty = selectedAddOns.filter(id => id === a.id).length;

                        if (a.id === 'seat_selection_performer') {
                          return (
                            <div key={a.id} className={`ptb-addon-item ${qty > 0 ? 'selected' : ''}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <span>{a.name}</span>
                                <span className="ptb-addon-price" style={{ marginLeft: '8px' }}>
                                  +{formatRupiah(a.price)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#2d3748', padding: '4px 8px', borderRadius: '4px' }}>
                                <button className="ptb-btn-ghost" style={{ padding: '2px 8px', minWidth: 'auto', border: 'none', fontSize: '1.2rem', lineHeight: 1 }} onClick={() => {
                                  if (qty > 0) {
                                    if (a.id === 'seat_selection_performer' && totalSelected > qty - 1) {
                                      Modal.warning({ title: 'Cannot Reduce Quantity', content: `Please deselect seats on the map first before reducing seat selection quantity.` });
                                      return;
                                    }
                                    const index = selectedAddOns.lastIndexOf(a.id);
                                    const newArr = [...selectedAddOns];
                                    newArr.splice(index, 1);
                                    setSelectedAddOns(newArr);
                                  }
                                }}>-</button>
                                <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 'bold' }}>{qty}</span>
                                <button className="ptb-btn-ghost" style={{ padding: '2px 8px', minWidth: 'auto', border: 'none', fontSize: '1.2rem', lineHeight: 1 }} onClick={() => {
                                  if (a.id === 'seat_selection_performer' && qty >= totalTicketsPurchased) {
                                    Modal.warning({ title: 'Limit Exceeded', content: `You cannot select more seats than the total tickets you are purchasing (${totalTicketsPurchased}).` });
                                    return;
                                  }
                                  setSelectedAddOns([...selectedAddOns, a.id]);
                                }}>+</button>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <label key={a.id} className={`ptb-addon-item ${qty > 0 ? 'selected' : ''}`} style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={qty > 0}
                              onChange={() => setSelectedAddOns(p => p.includes(a.id) ? p.filter(x => x !== a.id) : [...p, a.id])}
                            />
                            <span>{a.name}</span>
                            <span className="ptb-addon-price">+{formatRupiah(a.price)}</span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* 3. Seat Map (Only visible if applicable) */}
                {seatSelectionPerformerCount > 0 && (
                  <>
                    {seatLayoutLoading ? (
                      <div className="ptb-seat-loading"><div className="ptb-spinner" /><p>Loading seat map…</p></div>
                    ) : (
                      <div className="ptb-tier-section ptb-tier-section-fused">
                        <div className="ptb-tier-header">
                          <h3 className="ptb-section-label">Venue Map: {dynamicVenueInfo[venue]?.label}</h3>
                        </div>
                        {dynamicVenueInfo[venue]?.img && (
                          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <img src={dynamicVenueInfo[venue].img} alt="Venue Map" style={{ maxWidth: '48%', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }} />
                          </div>
                        )}
                        {seatDataMissing ? (
                          <div style={{ padding: '1.5rem', backgroundColor: '#742a2a', borderRadius: '8px', border: '1px solid #fc8181', textAlign: 'center' }}>
                            <p style={{ margin: 0, color: '#fed7d7', fontWeight: 600, fontSize: '1.1rem' }}>⚠️ No seats found for this session</p>
                            <p style={{ margin: '0.5rem 0 0 0', color: '#feb2b2', fontSize: '0.9rem' }}>
                              The seat data for <strong>{dynamicVenueInfo[venue]?.label || venue}</strong> — <strong>{date} | {session}</strong> could not be loaded.
                              Please contact the admin to generate seats for this session.
                            </p>
                          </div>
                        ) : (
                          <div className="ptb-seat-scroll">
                            <CustomSeatPicker
                              layout={unifiedLayout}
                              selectedSeats={Object.values(selections).flat()}
                              onSelect={handleSelectSeat}
                              onDeselect={handleDeselectSeat}
                              maxSeats={Infinity}
                              onSeatClick={null}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="ptb-seat-legend">
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%', marginBottom: '1rem', borderBottom: '1px solid #1f1f1f', paddingBottom: '1rem' }}>
                        {(event?.ticketTiers || []).filter(t => isTierAllowedForBuyer(t, buyerType)).map(t => {
                          const venuePrice = getVenuePrice(t);
                          return (
                            <span key={t.id} style={{ display: 'flex', alignItems: 'center', color: '#f0f0f0' }}>
                              <span className="ptb-legend-dot" style={{ backgroundColor: tierColors[t.id] || '#888', marginRight: '6px' }} />
                              {t.name} ({venuePrice !== null ? formatRupiah(venuePrice) : 'Price N/A'})
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot available" /> Available</span>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot selected" /> Selected</span>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot taken" /> Taken</span>
                      </div>
                    </div>

                  </>
                )}
              </div>

              <div className="ptb-fused-sidebar">
                <div className="ptb-cart-summary">
                  {renderCartSummaryInner()}
                </div>
              </div>
            </div>

            {hasMissingPricing && (
              <div style={{ padding: '1rem', backgroundColor: '#742a2a', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fc8181' }}>
                <p style={{ margin: 0, color: '#fed7d7' }}>
                  <strong>⚠️ Ticket pricing has not been configured for this venue yet.</strong> Please contact the event administrator. Checkout is disabled until pricing is set.
                </p>
              </div>
            )}

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
              <button className="ptb-btn-primary" disabled={!canProceedStep1} onClick={goNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* STEP Details */}
        {step === logicalStepDetails && (
          <div className="ptb-card ptb-animate">
            <h2 className="ptb-card-title">Your Details</h2>
            <div className="ptb-form">
              {buyerType === 'winner' && (
                <>
                  <label className="ptb-label">
                    Paying for Registrant {isWinnerEnsemble(selectedWinner) ? '(Ensemble)' : '(Winner)'}
                  </label>
                  <input
                    className="ptb-input"
                    value={registrantName}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', color: '#666', cursor: 'not-allowed' }}
                  />
                  {isWinnerEnsemble(selectedWinner) && getWinnerPerformerNames(selectedWinner).length > 0 && (
                    <div className="ptb-ensemble-details-box">
                      <span className="ptb-ensemble-label">
                        All Registered Performers ({getWinnerPerformerNames(selectedWinner).length}):
                      </span>
                      <div className="ptb-ensemble-names">
                        {getWinnerPerformerNames(selectedWinner).map((pName, idx) => (
                          <span key={idx} className="ptb-ensemble-member-tag">
                            {pName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <label className="ptb-label">Buyer Name *</label>
              <input className="ptb-input" placeholder="e.g. Andi Santoso (Parent)" value={buyerName} onChange={e => setBuyerName(e.target.value)} />

              <label className="ptb-label">Buyer Email Address *</label>
              <input className="ptb-input" type="email" placeholder="email@example.com" value={userEmail} onChange={e => setUserEmail(e.target.value)} />

              <label className="ptb-label">Buyer Phone Number *</label>
              <input className="ptb-input" type="tel" placeholder="+62 812 3456 7890" value={userPhone} onChange={e => setUserPhone(e.target.value)} />

              <p className="ptb-hint" style={{ marginTop: '0.5rem' }}>
                Your payment details and booking confirmation will be sent to this email.
              </p>
            </div>

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
              <button className="ptb-btn-primary" disabled={!canProceedStepDetails} onClick={goNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* STEP Review & Pay */}
        {step === logicalStepDetails + 1 && (
          <div className="ptb-card ptb-animate">
            <h2 className="ptb-card-title">Review & Pay</h2>

            <div className="ptb-summary">
              <div className="ptb-summary-row"><span>Venue</span><strong>{dynamicVenueInfo[venue]?.label || venue}</strong></div>
              <div className="ptb-summary-row"><span>Date</span><strong>{date}</strong></div>
              <div className="ptb-summary-row"><span>Session</span><strong>{session}</strong></div>
              <hr className="ptb-divider" />
              {buyerType === 'winner' ? (
                <>
                  <div className="ptb-summary-row">
                    <span>Winner Profile</span>
                    <strong>
                      {selectedWinner?.name}{' '}
                      <span className={`ptb-award-inline ${getBookingAwardLabel(selectedWinner?.finalAward).toLowerCase()}`}>
                        ({getBookingAwardLabel(selectedWinner?.finalAward)})
                      </span>
                      {isWinnerEnsemble(selectedWinner) && (
                        <span className="ptb-ensemble-badge" style={{ marginLeft: '6px' }}>
                          Ensemble
                        </span>
                      )}
                    </strong>
                  </div>
                  <div className="ptb-summary-row" style={{ alignItems: 'flex-start' }}>
                    <span>Paying For (Registrant)</span>
                    <div style={{ textAlign: 'right' }}>
                      <strong>{registrantName}</strong>
                      {isWinnerEnsemble(selectedWinner) && getWinnerPerformerNames(selectedWinner).length > 1 && (
                        <div className="ptb-ensemble-names" style={{ justifyContent: 'flex-end', marginTop: '4px' }}>
                          {getWinnerPerformerNames(selectedWinner).map((pName, idx) => (
                            <span key={idx} className="ptb-ensemble-member-tag" style={{ fontSize: '0.72rem' }}>
                              {pName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (<>
                <div className="ptb-summary-row"><span>Buyer Type</span><strong>Public Buyer</strong></div>
                {publicSelectionType === 'competition' && <div className="ptb-summary-row"><span>Selected Performance</span><strong>{registrantName}</strong></div>}
              </>)}
              {buyerType === 'winner' && <div className="ptb-summary-row"><span>Orchestra</span><strong>Free Seating · Redeem your orchestra ticket at the venue.</strong></div>}
              {buyerType === 'public' && publicSelectionType === 'competition' && <div className="ptb-summary-row"><span>Complimentary Orchestra</span><strong>{totalTicketsPurchased} place(s) · follows the selected performance after payment</strong></div>}
              {buyerType === 'public' && selectedSessionObj?.isOrchestra && <div className="ptb-summary-row"><span>Seating</span><strong>Free seating within your ticket category</strong></div>}
              <div className="ptb-summary-row"><span>Buyer Name</span><strong>{buyerName}</strong></div>
              <div className="ptb-summary-row"><span>Buyer Email</span><strong>{userEmail}</strong></div>
              <hr className="ptb-divider" />

              {ticketsPayload.map(t => (
                <div key={t.id} className="ptb-summary-row">
                  <span>{t.quantity}× {t.name}</span>
                  <strong>{formatRupiah(t.quantity * t.priceEach)}</strong>
                </div>
              ))}

              {availableAddOns.length > 0 && (() => {
                const uniqueAddOnIds = [...new Set(selectedAddOns)];
                return uniqueAddOnIds.map(addonId => {
                  const a = event?.addOns?.find(x => x.id === addonId);
                  if (!a) return null;
                  const qty = selectedAddOns.filter(id => id === addonId).length;
                  return (
                    <div key={a.id} className="ptb-summary-row">
                      <span>{qty}× {a.name}</span>
                      <strong>{formatRupiah(a.price * qty)}</strong>
                    </div>
                  );
                });
              })()}

              {allSelectedSeatIds.length > 0 && (
                <div className="ptb-summary-row ptb-seats-row" style={{ alignItems: 'flex-start' }}>
                  <span>
                    Seats
                  </span>
                  <strong style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {Object.values(selections).flat().map(s => s.tooltip?.split(' – ')[0]).join(', ')}
                  </strong>
                </div>
              )}

              <hr className="ptb-divider" />
              <div className="ptb-summary-row ptb-total-row">
                <span>Total</span>
                <strong className="ptb-gold">{formatRupiah(orderTotal)}</strong>
              </div>
            </div>

            <label className="ptb-hold-notice" style={{ cursor: 'pointer', marginBottom: '1rem' }}>
              <input type="checkbox" checked={manualPayment} onChange={event => setManualPayment(event.target.checked)} style={{ accentColor: '#EBBC64' }} />
              <span>Use international payment options (PayNow or bank transfer). We will email payment instructions; staff will confirm your payment.</span>
            </label>

            <div className="ptb-hold-notice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EBBC64" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <span>{manualPayment
                ? 'Your booking stays pending and its places remain reserved until APCS verifies your payment or cancels the booking.'
                : <>Your ticket reservation will be held for <strong>30 minutes</strong> after clicking Pay Now. Complete payment within this window to confirm your booking.</>}</span>
            </div>

            {submitError && <p className="ptb-error-msg">{submitError}</p>}

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack} disabled={isSubmitting}>← Back</button>
              <button className="ptb-btn-primary ptb-pay-btn" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? <span className="ptb-spinner-sm" /> : null}
                {isSubmitting ? 'Processing…' : manualPayment ? 'Reserve & Email Payment Instructions' : `Pay ${formatRupiah(orderTotal)}`}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
